/**
 * Làm rõ chữ watermark rất mờ (lớp chính 2–15%, lớp mã thứ hai ~1.5%) trên ảnh
 * chụp màn hình. Chạy hoàn toàn trong trình duyệt — ảnh bị lộ KHÔNG được tải
 * lên server.
 *
 *  - "local" (khuyên dùng): trừ nền trung bình quanh mỗi điểm (box blur bán
 *    kính r) rồi khuếch đại phần chênh. Nền phẳng — trắng hay tối — về xám
 *    giữa, còn nét chữ mờ nổi lên. Nét của nội dung thật cũng nổi, nhưng chữ
 *    watermark lặp đều theo một hướng nghiêng nên vẫn nhận ra.
 *  - "bright": kéo giãn vùng gần trắng (nền sáng của app/admin).
 *  - "dark": kéo giãn vùng gần đen (video LIVE, ảnh nền tối).
 */
export type RevealMode = 'local' | 'bright' | 'dark';

export interface RevealOptions {
  mode: RevealMode;
  /** Hệ số khuếch đại (4–60). */
  gain: number;
  /** Bán kính nền trung bình (px), chỉ cho "local". */
  radius: number;
  invert: boolean;
}

export function revealImage(source: ImageData, options: RevealOptions): ImageData {
  const { width, height, data } = source;
  const n = width * height;
  const lum = new Float32Array(n);
  for (let i = 0, p = 0; i < n; i += 1, p += 4) {
    lum[i] = 0.299 * data[p] + 0.587 * data[p + 1] + 0.114 * data[p + 2];
  }

  const out = new ImageData(width, height);
  const gain = Math.max(1, options.gain);
  let map: (value: number, index: number) => number;

  if (options.mode === 'local') {
    const mean = boxMean(lum, width, height, Math.max(1, Math.round(options.radius)));
    map = (value, index) => 128 + gain * (value - mean[index]);
  } else if (options.mode === 'bright') {
    const floor = 255 - 255 / gain;
    map = (value) => (value - floor) * gain;
  } else {
    map = (value) => value * gain;
  }

  const o = out.data;
  for (let i = 0, p = 0; i < n; i += 1, p += 4) {
    let v = map(lum[i], i);
    v = v < 0 ? 0 : v > 255 ? 255 : v;
    if (options.invert) v = 255 - v;
    o[p] = v;
    o[p + 1] = v;
    o[p + 2] = v;
    o[p + 3] = 255;
  }
  return out;
}

/** Trung bình trong ô vuông cạnh 2r+1 quanh mỗi điểm (ảnh tích phân, O(n)). */
function boxMean(values: Float32Array, width: number, height: number, r: number): Float32Array {
  const stride = width + 1;
  const integral = new Float64Array(stride * (height + 1));
  for (let y = 0; y < height; y += 1) {
    let rowSum = 0;
    for (let x = 0; x < width; x += 1) {
      rowSum += values[y * width + x];
      integral[(y + 1) * stride + (x + 1)] = integral[y * stride + (x + 1)] + rowSum;
    }
  }
  const mean = new Float32Array(width * height);
  for (let y = 0; y < height; y += 1) {
    const y0 = Math.max(0, y - r);
    const y1 = Math.min(height, y + r + 1);
    for (let x = 0; x < width; x += 1) {
      const x0 = Math.max(0, x - r);
      const x1 = Math.min(width, x + r + 1);
      const sum =
        integral[y1 * stride + x1] -
        integral[y0 * stride + x1] -
        integral[y1 * stride + x0] +
        integral[y0 * stride + x0];
      mean[y * width + x] = sum / ((x1 - x0) * (y1 - y0));
    }
  }
  return mean;
}
